/* istanbul ignore file */
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { AddToBasketLayerFragmentFragmentDoc } from '@packages/gql/generated/shopping/AddToBasketLayerFragmentFragmentDoc';
import type { LineItemFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { Box, Button, Card, CardContent, ErrorBoundary } from '@packages/shared';
import type { FC } from 'react';
import { Suspense } from 'react';
import { useIntl } from 'react-intl';
import { AddToBasketRecommendations } from '../AddToBasketRecommendations';
import { AddedProduct } from './AddedProduct';
import { AddedProductLoadingFallback } from './AddedProduct/AddedProductLoadingFallback';
import { addToBasketLayerMessages } from './messages';

/* GraphQL */ `
  fragment AddToBasketLayerFragment on Product {
    delivery {
      ...TaxHintFragment
    }
    ...AddToBasketRecommendations
  }
`;

type AddToBasketLayerProps = {
  lineItem: LineItemFragmentFragment;
  data: FragmentType<typeof AddToBasketLayerFragmentFragmentDoc>;
};

export const AddToBasketLayer: FC<AddToBasketLayerProps> = ({ lineItem, data }) => {
  const { formatMessage } = useIntl();

  const unmaskedData = getFragmentData(AddToBasketLayerFragmentFragmentDoc, data);

  const errorMessages = {
    text: formatMessage(addToBasketLayerMessages.errorMessage),
    button: formatMessage(addToBasketLayerMessages.errorButtonText),
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: [1.5, 1.5, 3],
        bgcolor: 'grey.light',
      }}
    >
      <Card>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <ErrorBoundary
            fallback={(reset) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {`${errorMessages.text} `}
                <Button onClick={reset}>{errorMessages.button}</Button>
              </Box>
            )}
          >
            <Suspense fallback={<AddedProductLoadingFallback />}>
              <AddedProduct lineItem={lineItem} taxHintData={unmaskedData.delivery} />
            </Suspense>
          </ErrorBoundary>
        </CardContent>
      </Card>

      <AddToBasketRecommendations data={unmaskedData} />
    </Box>
  );
};
